import { computed, onScopeDispose, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import {
  getDesktopBuiltinSkills,
  LOCAL_AUTHORIZATION_PROFILES,
  DESKTOP_RUNTIME_PROTOCOL_VERSION,
  type DesktopCapability,
  type DesktopDeviceView,
  type DesktopWorkspaceBindingView,
  type DesktopUpdateState,
  type DesktopBrowserState,
  type DesktopComputerAccessState,
  type ExecutionTargetPreference,
  type LocalAuthorizationProfile,
} from 'shared';
import { desktopApi } from '@/api/desktop.api';
import { getDesktopBridge } from '@/desktop';
import { useAuthStore } from './auth.store';
import { useAgentSessionStore } from './agent-session.store';

/** 桌面设备与目录绑定的前端唯一真源。 */
export const useDesktopStore = defineStore('desktop', () => {
  const workspaceId = ref<string | null>(null);
  const devices = ref<DesktopDeviceView[]>([]);
  const bindings = ref<DesktopWorkspaceBindingView[]>([]);
  const loading = ref(false);
  const error = ref('');
  const bridge = getDesktopBridge();
  const computerAccess = ref<DesktopComputerAccessState | null>(null);
  const computerError = ref('');
  async function refreshComputerAccess(request = false) {
    if (bridge?.platform !== 'darwin' || !bridge.computer) return;
    computerError.value = '';
    try {
      computerAccess.value = await bridge.computer[request ? 'request' : 'status']();
    } catch (cause) {
      if (!request) computerAccess.value = null;
      computerError.value =
        (cause as Error).message || '无法检查系统授权，请重新检查或重启客户端。';
    }
  }
  const authStore = useAuthStore();
  const executionPreference = ref<{
    mode: ExecutionTargetPreference['mode'];
    authorization: LocalAuthorizationProfile;
  }>({ mode: 'sandbox', authorization: 'on-request' });
  const executionPreferenceError = ref('');
  const executionPreferenceKey = computed(() =>
    bridge?.deviceId && authStore.user?.id
      ? `desktop-execution-preference:${JSON.stringify([bridge.apiOrigin, bridge.deviceId, authStore.user.id])}`
      : null,
  );
  const localExecutionTarget = computed<ExecutionTargetPreference>(() =>
    bridge && executionPreferenceKey.value
      ? {
          mode: 'desktop',
          deviceId: bridge.deviceId,
          authorization: executionPreference.value.authorization,
        }
      : { mode: 'sandbox' },
  );
  const defaultExecutionTarget = computed<ExecutionTargetPreference>(() =>
    executionPreference.value.mode === 'desktop' ? localExecutionTarget.value : { mode: 'sandbox' },
  );
  const taskCreationTarget = computed(() =>
    currentDevice.value?.online &&
    currentDevice.value.protocolVersion >= DESKTOP_RUNTIME_PROTOCOL_VERSION &&
    defaultBinding.value
      ? localExecutionTarget.value
      : undefined,
  );
  watch(
    executionPreferenceKey,
    (key) => {
      executionPreference.value = { mode: 'sandbox', authorization: 'on-request' };
      executionPreferenceError.value = '';
      if (!key) return;
      try {
        const stored: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
        if (!stored || typeof stored !== 'object') return;
        const { mode, authorization } = stored as Record<string, unknown>;
        const profile = LOCAL_AUTHORIZATION_PROFILES.find((value) => value === authorization);
        if ((mode === 'sandbox' || mode === 'desktop') && profile)
          executionPreference.value = { mode, authorization: profile };
      } catch {
        executionPreferenceError.value = '无法读取上次执行环境，请重新选择环境和授权级别。';
      }
    },
    { immediate: true, flush: 'sync' },
  );

  function rememberExecutionTarget(
    target: ExecutionTargetPreference,
    expectedKey = executionPreferenceKey.value,
  ): void {
    const key = executionPreferenceKey.value;
    if (
      !key ||
      key !== expectedKey ||
      (target.mode === 'desktop' && target.deviceId !== bridge?.deviceId)
    )
      return;
    executionPreference.value = {
      mode: target.mode,
      authorization:
        target.mode === 'desktop' ? target.authorization : executionPreference.value.authorization,
    };
    try {
      localStorage.setItem(key, JSON.stringify(executionPreference.value));
      executionPreferenceError.value = '';
    } catch {
      executionPreferenceError.value =
        '执行环境偏好保存失败，下次启动可能无法恢复，请检查本机存储。';
    }
  }
  const updateState = ref<DesktopUpdateState | null>(null);
  const updateError = ref('');
  let stopUpdates: (() => void) | null = null;
  function acceptUpdate(next: DesktopUpdateState) {
    if (!updateState.value || next.revision >= updateState.value.revision) updateState.value = next;
  }
  async function updateClient(action: 'status' | 'check' | 'download' | 'install') {
    if (!bridge?.updates) return;
    updateError.value = '';
    try {
      acceptUpdate(await bridge.updates[action]());
    } catch {
      updateError.value = '无法获取客户端更新状态，请重试或重启客户端。';
    }
  }
  function startUpdates() {
    if (!bridge?.updates || stopUpdates) return;
    stopUpdates = bridge.updates.onChange(acceptUpdate);
    void updateClient('status');
  }
  onScopeDispose(() => stopUpdates?.());
  let pendingRefresh: { workspaceId: string; promise: Promise<void> } | null = null;

  const currentDevice = computed(
    () => devices.value.find((device) => device.id === bridge?.deviceId) ?? null,
  );
  const currentDeviceBindings = computed(() =>
    bindings.value.filter((binding) => binding.deviceId === bridge?.deviceId),
  );
  const defaultBinding = computed(
    () => currentDeviceBindings.value.find((binding) => binding.isDefault) ?? null,
  );
  async function setTaskAuthorization(
    bindingId: string,
    authorization: LocalAuthorizationProfile | null,
  ): Promise<void> {
    if (!workspaceId.value) throw new Error('请先选择工作台');
    const updated = await desktopApi.setTaskAuthorization(
      workspaceId.value,
      bindingId,
      authorization,
    );
    bindings.value = bindings.value.map((binding) =>
      binding.id === updated.id ? updated : binding,
    );
  }
  const presenceTimer = bridge
    ? setInterval(() => {
        if (workspaceId.value && authStore.user)
          void refresh(workspaceId.value).catch(() => undefined);
      }, 30_000)
    : null;
  onScopeDispose(() => {
    if (presenceTimer) clearInterval(presenceTimer);
  });
  const builtinSkills = computed(() => {
    const device = currentDevice.value,
      binding = defaultBinding.value;
    return device && binding
      ? getDesktopBuiltinSkills({
          mode: 'desktop',
          deviceId: device.id,
          bindingId: binding.id,
          platform: device.platform,
          capabilities: binding.capabilities.filter((capability) =>
            device.capabilities.includes(capability),
          ),
          builtinSkills: device.builtinSkills,
          authorization: 'on-request',
        })
      : [];
  });
  const automationPaused = ref(false);
  const browserState = ref<DesktopBrowserState | null>(null);
  const browserError = ref('');
  let stopBrowser: (() => void) | null = null;
  let stopAnnotations: (() => void) | null = null;
  function acceptBrowser(next: DesktopBrowserState) {
    if (browserState.value && next.revision < browserState.value.revision) return;
    browserState.value = next;
    automationPaused.value = next.paused;
  }
  function startBrowser() {
    if (!bridge?.browser || stopBrowser) return;
    stopBrowser = bridge.browser.onChange(acceptBrowser);
    stopAnnotations =
      bridge.browser.onAnnotationSend?.(async (input) => {
        if (!authStore.user || authStore.user.id !== input.userId)
          throw new Error('登录账号已变化，批注未发送');
        return useAgentSessionStore().sendBrowserAnnotation(input);
      }) ?? null;
    void bridge.browser
      .status()
      .then(acceptBrowser)
      .catch(() => {
        browserError.value = '浏览器状态加载失败，请重新打开客户端。';
      });
  }
  async function openBrowser() {
    browserError.value = '';
    try {
      await bridge?.browser?.show();
    } catch (cause) {
      browserError.value = (cause as Error).message || '浏览器打开失败';
    }
  }
  onScopeDispose(() => {
    stopBrowser?.();
    stopAnnotations?.();
  });
  async function refreshAutomation() {
    if (bridge?.automation) automationPaused.value = (await bridge.automation.status()).paused;
  }
  async function toggleAutomation() {
    if (bridge?.automation)
      automationPaused.value = (
        await (automationPaused.value ? bridge.automation.resume() : bridge.automation.stop())
      ).paused;
  }

  function ensureWorkspace(nextWorkspaceId: string): void {
    if (workspaceId.value === nextWorkspaceId) return;
    workspaceId.value = nextWorkspaceId;
    devices.value = [];
    bindings.value = [];
    error.value = '';
  }

  async function refresh(nextWorkspaceId: string): Promise<void> {
    ensureWorkspace(nextWorkspaceId);
    if (!bridge) return;
    if (pendingRefresh?.workspaceId === nextWorkspaceId) return pendingRefresh.promise;
    const promise = refreshNow(nextWorkspaceId);
    pendingRefresh = { workspaceId: nextWorkspaceId, promise };
    try {
      await promise;
    } finally {
      if (pendingRefresh?.promise === promise) pendingRefresh = null;
    }
  }

  async function refreshNow(nextWorkspaceId: string): Promise<void> {
    loading.value = true;
    error.value = '';
    try {
      if (bridge?.platform === 'darwin' && bridge.computer) await refreshComputerAccess();
      const [nextDevices, nextBindings] = await Promise.all([
        desktopApi.devices(),
        desktopApi.bindings(nextWorkspaceId),
      ]);
      if (workspaceId.value !== nextWorkspaceId) return;
      devices.value = nextDevices;
      bindings.value = nextBindings;
    } catch (cause) {
      error.value = (cause as { message?: string }).message ?? '加载本机执行环境失败';
      throw cause;
    } finally {
      if (workspaceId.value === nextWorkspaceId) loading.value = false;
    }
  }

  async function createBinding(
    nextWorkspaceId: string,
    input: { path: string; displayName: string; capabilities: DesktopCapability[] },
  ): Promise<DesktopWorkspaceBindingView> {
    if (!bridge) throw new Error('请在电脑客户端中添加本机目录');
    ensureWorkspace(nextWorkspaceId);
    const binding = await desktopApi.createBinding(nextWorkspaceId, {
      deviceId: bridge.deviceId,
      displayName: input.displayName,
      capabilities: input.capabilities,
      isDefault: !currentDeviceBindings.value.length,
    });
    try {
      await bridge.directories.bind(binding.id, input.path);
    } catch (cause) {
      await desktopApi.removeBinding(nextWorkspaceId, binding.id).catch(() => undefined);
      throw cause;
    }
    await refresh(nextWorkspaceId);
    return binding;
  }

  async function setDefault(nextWorkspaceId: string, bindingId: string): Promise<void> {
    ensureWorkspace(nextWorkspaceId);
    await desktopApi.setDefaultBinding(nextWorkspaceId, bindingId);
    await refresh(nextWorkspaceId);
  }

  async function removeBinding(nextWorkspaceId: string, bindingId: string): Promise<void> {
    ensureWorkspace(nextWorkspaceId);
    await desktopApi.removeBinding(nextWorkspaceId, bindingId);
    await bridge?.directories.remove(bindingId).catch(() => undefined);
    await refresh(nextWorkspaceId);
  }

  return {
    computerAccess,
    computerError,
    refreshComputerAccess,
    workspaceId,
    executionPreferenceKey,
    defaultExecutionTarget,
    taskCreationTarget,
    localExecutionTarget,
    executionPreferenceError,
    rememberExecutionTarget,
    updateState,
    updateError,
    startUpdates,
    updateClient,
    devices,
    bindings,
    loading,
    error,
    currentDevice,
    currentDeviceBindings,
    defaultBinding,
    builtinSkills,
    automationPaused,
    browserState,
    browserError,
    startBrowser,
    openBrowser,
    refreshAutomation,
    toggleAutomation,
    refresh,
    createBinding,
    setDefault,
    setTaskAuthorization,
    removeBinding,
  };
});
